import React, { useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import { FileText, X, Users, CheckCircle2, AlertCircle, TrendingUp, Printer, MessageSquare, Mail } from 'lucide-react';

// Print-only styles injected when report is printed
const PRINT_STYLES = `
@media print {
  body > * { display: none !important; }
  #ward-report-printable { display: block !important; }
  #ward-report-printable * { visibility: visible !important; }
}
`;

const WARDS = ['ward-1','ward-2','ward-3','ward-4','ward-5','ward-6','ward-7','ward-8','ward-9','ward-10'];

const PERIODS = [
  { label: 'Last Month', months: 1 },
  { label: 'Last Quarter (3 months)', months: 3 },
  { label: 'Last 6 Months', months: 6 },
  { label: 'Last Year', months: 12 },
];

const WardReportModal = ({ onClose }) => {
  const [ward, setWard] = useState('ward-1');
  const [months, setMonths] = useState(3);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePrint = () => {
    const printContent = document.getElementById('ward-report-printable');
    if (!printContent) return;
    
    const sanitizedHTML = DOMPurify.sanitize(printContent.innerHTML);
    const win = window.open('', '_blank');
    if (!win) return;
    
    win.document.open();
    win.document.write(`<!DOCTYPE html><html><head><title>Ward Report</title>
      <style>
        body{font-family:system-ui,sans-serif;background:#fff;color:#000;padding:32px;max-width:800px;margin:0 auto}
        h1{font-size:1.5rem;margin-bottom:4px} h2{font-size:1rem;color:#555;border-bottom:1px solid #ddd;padding-bottom:4px;margin-top:24px}
        .stat-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:12px}
        .stat-box{border:1px solid #ddd;border-radius:4px;padding:12px}
        .stat-val{font-size:1.5rem;font-weight:bold} .stat-label{font-size:0.75rem;color:#777;margin-top:2px}
        table{width:100%;border-collapse:collapse;font-size:0.85rem;margin-top:8px}
        th{background:#f5f5f5;text-align:left;padding:6px 8px;border:1px solid #ddd}
        td{padding:6px 8px;border:1px solid #ddd}
        .footer{margin-top:32px;font-size:0.7rem;color:#aaa;text-align:center;border-top:1px solid #ddd;padding-top:12px}
      </style></head><body>${sanitizedHTML}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const buildMailtoLink = (report) => {
    const subject = encodeURIComponent(`CYQR Ward Report — ${(report.ward || '').replace('-', ' ').toUpperCase()}`);
    const body = encodeURIComponent(
      `Hello ${report.councillor?.name || 'Councillor'},\n\n` +
      `Please find the quarterly civic activity summary for ${(report.ward || '').replace('-', ' ').toUpperCase()} below:\n\n` +
      `Period: ${report.period?.start} to ${report.period?.end}\n` +
      `Registered Residents: ${report.registered_users || 0}\n` +
      `Total Posts: ${report.posts?.total || 0} (${report.posts?.resolved || 0} resolved, ${report.posts?.resolution_rate || 0}% resolution rate)\n` +
      `Issue Posts: ${report.posts?.issues || 0}\n\n` +
      `This data has been compiled from resident-submitted reports on CYQR.CA.\n\n` +
      `Please review and respond to pending issues in your ward.\n\nSent via CYQR Platform`
    );
    const email = report.councillor?.email || '';
    return `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const generate = async () => {
    setLoading(true);
    setReport(null);
    try {
      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - months * 30 * 24 * 60 * 60 * 1000).toISOString();
      const res = await api.get(`/admin/ward-report/${ward}?start_date=${startDate}&end_date=${endDate}`);
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const StatBox = ({ icon: Icon, label, value, color = 'text-white', sub }) => (
    <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
        <p className={`font-bold text-lg ${color}`}>{value ?? '—'}</p>
      </div>
      <p className="text-xs text-gray-500">{label}</p>
      {sub && <p className="text-xs text-gray-600 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-[#111] border border-[#2a2a2a] rounded-lg w-full max-w-3xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#2a2a2a]">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-[#C5A52A]" />
            <h2 className="text-white font-bold text-lg">Quarterly Ward Report</h2>
          </div>
          <div className="flex items-center gap-2">
            {report && (
              <>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-3 py-1.5 bg-[#1a1a1a] rounded transition-colors"
                  data-testid="print-report-btn"
                >
                  <Printer className="w-3.5 h-3.5" /> Print / Save PDF
                </button>
                {report.councillor?.email && (
                  <a
                    href={buildMailtoLink(report)}
                    className="flex items-center gap-1.5 text-xs text-[#C5A52A] hover:text-yellow-300 px-3 py-1.5 bg-[#C5A52A]/10 border border-[#C5A52A]/30 rounded transition-colors"
                    data-testid="email-councillor-btn"
                    title={`Open email to ${report.councillor.name}`}
                  >
                    <Mail className="w-3.5 h-3.5" /> Email Councillor
                  </a>
                )}
              </>
            )}
            <button onClick={onClose} className="text-gray-500 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="p-5 border-b border-[#2a2a2a] flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Ward</label>
            <select
              value={ward}
              onChange={e => setWard(e.target.value)}
              className="bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white"
              data-testid="ward-report-ward-select"
              style={{ colorScheme: 'dark' }}
            >
              {WARDS.map(w => (
                <option key={w} value={w} style={{ background: '#111', color: '#fff' }}>{w.replace('-', ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Period</label>
            <select
              value={months}
              onChange={e => setMonths(parseInt(e.target.value))}
              className="bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white"
              data-testid="ward-report-months-select"
              style={{ colorScheme: 'dark' }}
            >
              {PERIODS.map(p => (
                <option key={p.months} value={p.months} style={{ background: '#111', color: '#fff' }}>{p.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-ward-report-btn"
            className="flex items-center gap-2 px-5 py-2 bg-[#C5A52A] text-black font-bold rounded text-sm hover:bg-yellow-400 disabled:opacity-50"
          >
            <FileText className="w-4 h-4" />
            {loading ? 'Compiling…' : 'Generate Report'}
          </button>
        </div>

        {/* Report Output */}
        {report && (
          <div className="p-6 space-y-6" data-testid="ward-report-output" id="ward-report-printable">
            {/* Title */}
            <div className="text-center border-b border-[#2a2a2a] pb-5">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">City of Regina — CYQR Platform</p>
              <h1 className="text-xl font-bold text-white">{report.ward?.replace('-', ' ').toUpperCase()} — Civic Activity Report</h1>
              {report.period && (
                <p className="text-gray-500 text-sm mt-1">
                  {report.period.start} → {report.period.end}
                </p>
              )}
              {report.councillor?.name && (
                <p className="text-[#C5A52A] text-sm mt-2 font-mono">Councillor: {report.councillor.name}</p>
              )}
            </div>

            {/* Residents */}
            <div>
              <h2 className="text-xs text-gray-400 uppercase tracking-wide font-bold mb-2">Resident Engagement</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatBox icon={Users} label="Registered Residents" value={report.registered_users} color="text-blue-400" />
                <StatBox icon={AlertCircle} label="Total Posts" value={report.posts?.total} color="text-yellow-400" />
                <StatBox icon={CheckCircle2} label="Resolved Posts" value={report.posts?.resolved} color="text-green-400"
                  sub={`${report.posts?.resolution_rate ?? 0}% resolution rate`} />
                <StatBox icon={TrendingUp} label="Issue Posts" value={report.posts?.issues} color="text-orange-400" />
                <StatBox icon={TrendingUp} label="Positive Posts" value={report.posts?.positive} color="text-green-400" />
                <StatBox icon={MessageSquare} label="Discussions" value={report.posts?.discussions} color="text-purple-400" />
              </div>
            </div>

            {/* Structured Issues */}
            {report.structured_issues && (
              <div>
                <h2 className="text-xs text-gray-400 uppercase tracking-wide font-bold mb-2">Tracked Issues</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatBox icon={AlertCircle} label="Total Issues" value={report.structured_issues.total} color="text-yellow-400" />
                  <StatBox icon={CheckCircle2} label="Fixed" value={report.structured_issues.fixed} color="text-green-400"
                    sub={`${report.structured_issues.fix_rate ?? 0}% fix rate`} />
                  <StatBox icon={AlertCircle} label="Pending" value={report.structured_issues.pending} color="text-red-400" />
                  <StatBox icon={CheckCircle2} label="Verified" value={report.structured_issues.verified} color="text-blue-400" />
                </div>

                {report.structured_issues.by_category && Object.keys(report.structured_issues.by_category).length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Issues by Category</p>
                    <div className="space-y-1">
                      {Object.entries(report.structured_issues.by_category).map(([cat, counts]) => (
                        <div key={cat} className="flex items-center justify-between bg-[#0a0a0a] border border-[#1a1a1a] rounded px-3 py-2">
                          <span className="text-sm text-gray-300">{cat}</span>
                          <div className="flex gap-4 text-xs font-mono">
                            <span className="text-gray-500">Total: {counts.total}</span>
                            <span className="text-green-400">Fixed: {counts.fixed}</span>
                            <span className="text-red-400">Pending: {counts.pending}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Councillor Messages */}
            {report.messages && (
              <div>
                <h2 className="text-xs text-gray-400 uppercase tracking-wide font-bold mb-2">Councillor Communications</h2>
                <div className="grid grid-cols-3 gap-3">
                  <StatBox icon={MessageSquare} label="Messages Sent" value={report.messages.total} color="text-blue-400" />
                  <StatBox icon={CheckCircle2} label="Replied" value={report.messages.replied} color="text-green-400"
                    sub={`${report.messages.response_rate ?? 0}% response rate`} />
                  <StatBox icon={AlertCircle} label="Awaiting Reply" value={(report.messages.total || 0) - (report.messages.replied || 0)} color="text-yellow-400" />
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-[#2a2a2a] pt-4 text-center">
              <p className="text-xs text-gray-600 font-mono">
                Generated by CYQR — Show Them Your Regina · {new Date(report.generated_at || Date.now()).toLocaleDateString()}
              </p>
              <p className="text-[10px] text-gray-700 mt-1">
                Data sourced from resident-submitted reports on CYQR.CA. For informational purposes only.
              </p>
            </div>
          </div>
        )}

        {!report && !loading && (
          <div className="p-8 text-center text-gray-600">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Select a ward and period, then click Generate Report.</p>
          </div>
        )}
        {loading && (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#C5A52A] mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Compiling ward data…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WardReportModal;
